import { useMemo, useState } from "react";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, ShieldCheck, ShieldOff, UserCheck, Users, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CR_ROLE_NAMES } from "@/lib/crClassMapping";

// --- Domain config -----------------------------------------------------------
// CR roles map directly to (program, NTA level). Keep this in one place so the
// validation schema and the role string are always in sync.
const PROGRAMS = [
  { value: "cmt", label: "CMT — Clinical Medicine" },
  { value: "pst", label: "PST — Pharmaceutical Sciences" },
  { value: "sw", label: "SW — Social Work" },
] as const;

const NTA_LEVELS = [4, 5, 6] as const;

const programSchema = z.enum(["cmt", "pst", "sw"], {
  errorMap: () => ({ message: "Select a valid program" }),
});
const ntaLevelSchema = z
  .number({ invalid_type_error: "Select an NTA level" })
  .int()
  .refine((n) => (NTA_LEVELS as readonly number[]).includes(n), {
    message: "NTA level must be 4, 5 or 6",
  });

const assignSchema = z.object({
  program: programSchema,
  ntaLevel: ntaLevelSchema,
  userIds: z.array(z.string().uuid()).min(1, { message: "Pick at least one student" }),
});

type AssignInput = z.infer<typeof assignSchema>;

const buildCrRole = (program: AssignInput["program"], nta: number) =>
  `cr_${program}${nta}` as const;

const labelForRole = (role: string) => role.replace(/^cr_/, "CR ").toUpperCase();

// --- Component ---------------------------------------------------------------
export function AssignCrRoleTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [program, setProgram] = useState<AssignInput["program"] | "">("");
  const [ntaLevel, setNtaLevel] = useState<number | "">("");
  const [studentSearch, setStudentSearch] = useState("");
  const [pickedUserIds, setPickedUserIds] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<{
    role: string;
    students: { userId: string; fullName: string }[];
  } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<{
    userId: string;
    fullName: string;
    role: string;
  } | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

  // Students list — sourced directly from the `students` table (the source of
  // truth for who is a student) so we don't depend on `user_roles` SELECT
  // visibility, which is restricted by RLS for non-privileged callers and
  // caused the picker to come back empty.
  const { data: students = [], isLoading: loadingStudents } = useQuery({
    queryKey: ["cr-assign-students"],
    queryFn: async () => {
      const { data: studentRecs, error } = await supabase
        .from("students")
        .select("user_id, student_number, class, program, nta_level, first_name, last_name")
        .not("user_id", "is", null);
      if (error) throw error;

      const userIds = Array.from(
        new Set((studentRecs ?? []).map((s: any) => s.user_id).filter(Boolean)),
      );

      // Pull profiles in chunks to avoid URL length issues on large cohorts.
      const profileMap = new Map<string, { full_name: string | null }>();
      for (let i = 0; i < userIds.length; i += 200) {
        const slice = userIds.slice(i, i + 200);
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, full_name")
          .in("user_id", slice);
        (profs ?? []).forEach((p: any) => profileMap.set(p.user_id, p));
      }

      return (studentRecs ?? [])
        .map((s: any) => {
          const profile = profileMap.get(s.user_id);
          const fallbackName = [s.first_name, s.last_name].filter(Boolean).join(" ").trim();
          return {
            user_id: s.user_id,
            full_name: profile?.full_name || fallbackName || "Unnamed",
            studentRecord: {
              student_number: s.student_number,
              class: s.class,
              program: s.program,
              level: s.nta_level,
            },
          };
        })
        .sort((a: any, b: any) => (a.full_name || "").localeCompare(b.full_name || ""));
    },
  });

  // Existing CR assignments (any cr_* role), so we can show & revoke.
  const { data: currentCrs = [] } = useQuery({
    queryKey: ["current-cr-assignments"],
    queryFn: async () => {
      const { data: roles, error } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .in("role", [...CR_ROLE_NAMES] as any);
      if (error) throw error;

      const userIds = Array.from(new Set((roles ?? []).map((r: any) => r.user_id)));
      if (userIds.length === 0) return [];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);
      const profMap = new Map((profiles ?? []).map((p: any) => [p.user_id, p]));
      return (roles ?? []).map((r: any) => ({
        user_id: r.user_id,
        role: r.role,
        full_name: profMap.get(r.user_id)?.full_name ?? "Unknown",
        email: null as string | null,
      }));
    },
  });

  // Filter students by selected program/level + free-text search.
  const filteredStudents = useMemo(() => {
    const expectedClassToken = program && ntaLevel ? `${program}${ntaLevel}`.toUpperCase() : null;
    const q = studentSearch.trim().toLowerCase();
    return students.filter((s: any) => {
      if (q) {
        const hay = `${s.full_name ?? ""} ${s.studentRecord?.student_number ?? ""} ${s.studentRecord?.class ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (expectedClassToken) {
        // Normalise stored class (e.g. "CMT 4" → "CMT4") for comparison.
        const clsNorm = (s.studentRecord?.class ?? "")
          .toUpperCase()
          .replace(/\s+/g, "");
        // Structured fallback for legacy rows where `class` is missing or
        // formatted differently (e.g. program: "Clinical Medicine", nta_level: "6").
        const progMatch = (s.studentRecord?.program ?? "")
          .toLowerCase()
          .includes(program);
        const levelMatch = String(s.studentRecord?.level ?? "") === String(ntaLevel);
        return (
          clsNorm === expectedClassToken ||
          clsNorm.includes(expectedClassToken) ||
          (progMatch && levelMatch)
        );
      }
      return true;
    });
  }, [students, program, ntaLevel, studentSearch]);

  const assignMutation = useMutation({
    mutationFn: async ({ userIds, role }: { userIds: string[]; role: string }) => {
      const failures: { userId: string; message: string }[] = [];
      setBulkProgress({ done: 0, total: userIds.length });
      let done = 0;
      let alreadyAssigned = 0;
      for (const userId of userIds) {
        const { error } = await supabase
          .from("user_roles")
          .insert({ user_id: userId, role: role as any });
        if (error) {
          // Treat duplicate (already assigned) as success — idempotent assignment.
          if (error.code === "23505" || /duplicate key/i.test(error.message)) {
            alreadyAssigned += 1;
          } else {
            failures.push({ userId, message: error.message });
          }
        }
        done += 1;
        setBulkProgress({ done, total: userIds.length });
      }
      return { successCount: userIds.length - failures.length, failures, alreadyAssigned };
    },
    onSuccess: ({ successCount, failures, alreadyAssigned }) => {
      const dupeNote = alreadyAssigned > 0 ? ` (${alreadyAssigned} already had the role)` : "";
      if (failures.length === 0) {
        toast({ title: `Assigned CR role to ${successCount} student${successCount === 1 ? "" : "s"}${dupeNote}` });
      } else {
        toast({
          title: `Assigned ${successCount}, ${failures.length} failed${dupeNote}`,
          description: failures[0]?.message,
          variant: successCount === 0 ? "destructive" : "default",
        });
      }
      setPickedUserIds(new Set());
      setConfirm(null);
      setBulkProgress(null);
      queryClient.invalidateQueries({ queryKey: ["current-cr-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["users-with-roles"] });
    },
    onError: (e: any) => {
      setBulkProgress(null);
      toast({ title: "Could not assign", description: e.message, variant: "destructive" });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId)
        .eq("role", role as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "CR role revoked" });
      setRevokeTarget(null);
      queryClient.invalidateQueries({ queryKey: ["current-cr-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["users-with-roles"] });
    },
    onError: (e: any) =>
      toast({ title: "Could not revoke", description: e.message, variant: "destructive" }),
  });

  const handleAssignClick = () => {
    const parsed = assignSchema.safeParse({
      program,
      ntaLevel: typeof ntaLevel === "number" ? ntaLevel : Number(ntaLevel),
      userIds: Array.from(pickedUserIds),
    });
    if (!parsed.success) {
      const first = parsed.error.errors[0];
      toast({
        title: "Check your selection",
        description: first?.message ?? "Invalid input",
        variant: "destructive",
      });
      return;
    }
    const role = buildCrRole(parsed.data.program, parsed.data.ntaLevel);

    const skip = new Set(
      currentCrs.filter((c) => c.role === role).map((c) => c.user_id),
    );
    const eligibleIds = parsed.data.userIds.filter((id) => !skip.has(id));
    const skipped = parsed.data.userIds.length - eligibleIds.length;

    if (eligibleIds.length === 0) {
      toast({
        title: "Already assigned",
        description: `All selected students already hold ${labelForRole(role)}.`,
      });
      return;
    }

    const studentSummaries = eligibleIds.map((id) => {
      const s = students.find((x: any) => x.user_id === id);
      return { userId: id, fullName: s?.full_name ?? "Unknown" };
    });

    if (skipped > 0) {
      toast({
        title: `${skipped} already assigned`,
        description: `Skipping ${skipped} student${skipped === 1 ? "" : "s"} who already hold this role.`,
      });
    }

    setConfirm({ role, students: studentSummaries });
  };

  const targetClassPreview =
    program && ntaLevel ? `${program.toUpperCase()}${ntaLevel}` : null;

  return (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>CR vs Group Leader — what's the difference?</AlertTitle>
        <AlertDescription className="space-y-1.5 mt-1">
          <p>
            <strong>Class Representative (CR)</strong> — scans the <strong>Academic QR code</strong> to mark{" "}
            <strong>session-based</strong> attendance in class, only while a session is open.
          </p>
          <p>
            <strong>Group Leader</strong> — scans the <strong>Academic QR code</strong> to mark{" "}
            <strong>rotation-based</strong> attendance at field sites. Each group leader is bound to one field group with
            its own <strong>geofence</strong> and <strong>scan time window</strong>. Assign Group Leaders inside{" "}
            <strong>Attendance → Rotation → Groups</strong>.
          </p>
        </AlertDescription>
      </Alert>

      {/* Selectors */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Assign Class Representative / Group Leader
          </CardTitle>
          <CardDescription>
            This screen assigns the <strong>CR</strong> role (class-wide session scanning). Pick a program and NTA level,
            then choose a student from that class to grant the matching CR role. Group Leaders are appointed{" "}
            <strong>per field group</strong> in the Attendance → Rotation module.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cr-program">Program</Label>
              <Select
                value={program}
                onValueChange={(v) => {
                  setProgram(v as AssignInput["program"]);
                  setPickedUserIds(new Set());
                }}
              >
                <SelectTrigger id="cr-program"><SelectValue placeholder="Select program" /></SelectTrigger>
                <SelectContent>
                  {PROGRAMS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cr-level">NTA Level</Label>
              <Select
                value={ntaLevel === "" ? "" : String(ntaLevel)}
                onValueChange={(v) => {
                  const n = Number(v);
                  // Defensive parse — reject anything outside the allowed list.
                  const parsed = ntaLevelSchema.safeParse(n);
                  if (!parsed.success) {
                    toast({
                      title: "Invalid NTA level",
                      description: parsed.error.errors[0]?.message,
                      variant: "destructive",
                    });
                    return;
                  }
                  setNtaLevel(parsed.data);
                  setPickedUserIds(new Set());
                }}
              >
                <SelectTrigger id="cr-level"><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent>
                  {NTA_LEVELS.map((n) => (
                    <SelectItem key={n} value={String(n)}>NTA Level {n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Resulting role</Label>
              <div className="h-10 px-3 rounded-md border bg-muted/30 flex items-center text-sm">
                {targetClassPreview ? (
                  <Badge variant="outline" className="font-mono text-xs">
                    cr_{program}{ntaLevel}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground text-xs">Choose program & level</span>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cr-search">Find student</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="cr-search"
                className="pl-9"
                placeholder={
                  targetClassPreview
                    ? `Search students in ${targetClassPreview}…`
                    : "Search by name, student no., or class…"
                }
                value={studentSearch}
                maxLength={100}
                onChange={(e) => setStudentSearch(e.target.value)}
              />
            </div>
            {targetClassPreview && (
              <p className="text-[11px] text-muted-foreground">
                Showing students whose class matches <strong>{targetClassPreview}</strong>.
              </p>
            )}
          </div>

          <div className="border rounded-md max-h-64 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    {(() => {
                      const visible = filteredStudents.slice(0, 50);
                      const allChecked =
                        visible.length > 0 && visible.every((s: any) => pickedUserIds.has(s.user_id));
                      const someChecked = visible.some((s: any) => pickedUserIds.has(s.user_id));
                      return (
                        <Checkbox
                          checked={allChecked ? true : someChecked ? "indeterminate" : false}
                          onCheckedChange={(checked) => {
                            const next = new Set(pickedUserIds);
                            if (checked) visible.forEach((s: any) => next.add(s.user_id));
                            else visible.forEach((s: any) => next.delete(s.user_id));
                            setPickedUserIds(next);
                          }}
                          aria-label="Select all visible students"
                        />
                      );
                    })()}
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Student No.</TableHead>
                  <TableHead>Class</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingStudents && (
                  <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-6">Loading students…</TableCell></TableRow>
                )}
                {!loadingStudents && filteredStudents.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-6">No students match the current filters.</TableCell></TableRow>
                )}
                {filteredStudents.slice(0, 50).map((s: any) => {
                  const isPicked = pickedUserIds.has(s.user_id);
                  const toggle = () => {
                    const next = new Set(pickedUserIds);
                    if (isPicked) next.delete(s.user_id);
                    else next.add(s.user_id);
                    setPickedUserIds(next);
                  };
                  return (
                    <TableRow
                      key={s.user_id}
                      className={isPicked ? "bg-primary/5" : "cursor-pointer hover:bg-muted/50"}
                      onClick={toggle}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isPicked}
                          onCheckedChange={toggle}
                          aria-label={`Pick ${s.full_name}`}
                        />
                      </TableCell>
                      <TableCell className="text-sm font-medium">{s.full_name || "Unnamed"}</TableCell>
                      <TableCell className="font-mono text-xs">{s.studentRecord?.student_number || "—"}</TableCell>
                      <TableCell className="text-xs">
                        {s.studentRecord?.class ? (
                          <Badge variant="outline" className="text-[10px]">{s.studentRecord.class}</Badge>
                        ) : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredStudents.length > 50 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-[11px] text-muted-foreground py-2">Showing first 50 — refine your search to narrow down.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Users className="h-3.5 w-3.5" />
              {pickedUserIds.size} student{pickedUserIds.size === 1 ? "" : "s"} selected
              {pickedUserIds.size > 0 && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => setPickedUserIds(new Set())}
                >
                  Clear
                </Button>
              )}
            </div>
            <Button
              onClick={handleAssignClick}
              disabled={!program || !ntaLevel || pickedUserIds.size === 0 || assignMutation.isPending}
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Assign CR Role{pickedUserIds.size > 1 ? `s (${pickedUserIds.size})` : ""}
            </Button>
          </div>

        </CardContent>
      </Card>

      {/* Existing CRs */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg">Current Class Representatives</CardTitle>
          <CardDescription>
            {currentCrs.length} active CR assignment{currentCrs.length === 1 ? "" : "s"}.
          </CardDescription>
        </CardHeader>
        <CardContent className="table-responsive">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>CR Role</TableHead>
                <TableHead className="hidden sm:table-cell">Email</TableHead>
                <TableHead className="w-24 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentCrs.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-6">No CR roles assigned yet.</TableCell></TableRow>
              )}
              {currentCrs
                .sort((a, b) => a.role.localeCompare(b.role) || a.full_name.localeCompare(b.full_name))
                .map((c) => (
                  <TableRow key={`${c.user_id}-${c.role}`}>
                    <TableCell className="font-medium text-sm">{c.full_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-[10px]">{c.role}</Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-xs text-muted-foreground">{c.email ?? "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setRevokeTarget({ userId: c.user_id, fullName: c.full_name, role: c.role })}
                      >
                        <ShieldOff className="h-3.5 w-3.5 mr-1" />
                        Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Confirm bulk assign */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o && !assignMutation.isPending) setConfirm(null); }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Assign {confirm && labelForRole(confirm.role)} to {confirm?.students.length} student{confirm?.students.length === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Each student below will receive the <code className="font-mono">{confirm?.role}</code> role and gain CR-level access for this class. Their student role is preserved.
                </p>
                <ScrollArea className="max-h-48 rounded-md border bg-muted/30 p-2">
                  <ul className="space-y-1">
                    {confirm?.students.map((s) => (
                      <li key={s.userId} className="text-xs flex items-center gap-2">
                        <ShieldCheck className="h-3 w-3 text-primary shrink-0" />
                        <span className="truncate">{s.fullName}</span>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
                {bulkProgress && (
                  <p className="text-xs text-muted-foreground">
                    Progress: {bulkProgress.done} / {bulkProgress.total}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={assignMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={assignMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!confirm) return;
                assignMutation.mutate({
                  userIds: confirm.students.map((s) => s.userId),
                  role: confirm.role,
                });
              }}
            >
              {assignMutation.isPending
                ? `Assigning… ${bulkProgress ? `(${bulkProgress.done}/${bulkProgress.total})` : ""}`
                : `Assign to ${confirm?.students.length ?? 0}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm revoke */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => { if (!o) setRevokeTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke {revokeTarget && labelForRole(revokeTarget.role)}?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{revokeTarget?.fullName}</strong> will lose CR-level access for this class. Their student account is unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={revokeMutation.isPending}
              onClick={() => revokeTarget && revokeMutation.mutate({ userId: revokeTarget.userId, role: revokeTarget.role })}
            >
              {revokeMutation.isPending ? "Revoking…" : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
